import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from './Navbar';
import Footer from './Footer';
import authService from '../services/authService';

const PartnerWithUs = ({ isModal = false, onClose = null, onClubCreated = null }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    location: '',
    description: '',
    games: '',
    meetingDay: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleFileChange = (file) => {
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        setError('Please upload an image file (JPEG, PNG, GIF, or WebP)');
        return;
      }

      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        setError('Image size must be less than 5MB');
        return;
      }

      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
      setError('');
    }
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files[0];
    handleFileChange(file);
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileChange(e.dataTransfer.files[0]);
    }
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Check if user is authenticated
    const token = localStorage.getItem('accessToken');
    if (!token) {
      // Store the intent to create a club, then redirect to login
      localStorage.setItem('pendingClubRegistration', JSON.stringify({
        formData,
        imagePreview
      }));
      navigate('/login?redirect=partner-with-us');
      return;
    }

    setLoading(true);

    try {
      const apiUrl = process.env.NODE_ENV === 'production' 
        ? '/api' 
        : (process.env.REACT_APP_API_URL || 'http://localhost:5000/api');

      // Create FormData for file upload
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('location', formData.location);
      formDataToSend.append('description', formData.description);
      formDataToSend.append('games', formData.games);
      formDataToSend.append('meetingDay', formData.meetingDay);
      
      if (imageFile) {
        formDataToSend.append('image', imageFile);
      }

      const response = await fetch(`${apiUrl}/clubs`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formDataToSend
      });

      const result = await response.json();

      if (response.status === 401 || response.status === 403) {
        setError('Authentication failed. Please login again.');
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        setTimeout(() => navigate('/login'), 2000);
        return;
      }

      if (response.ok && result.success) {
        setSubmitted(true);
        // Clear the pending registration
        localStorage.removeItem('pendingClubRegistration');
      } else {
        setError(result.message || 'Failed to register club');
      }
    } catch (err) {
      console.error('Error registering club:', err);
      setError('An error occurred while registering the club: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      <div className="flex-grow bg-gray-50 py-12">
        <div className="container mx-auto px-4 max-w-6xl">
          {/* Hero Section */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Partner with BoardVoyage
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Join our growing network of board game clubs and connect with passionate players in your area
            </p>
          </div>

          {/* Benefits Section */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <div className="card bg-base-100 shadow-md">
              <div className="card-body items-center text-center">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                  <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <h3 className="card-title text-lg mb-2">Reach More Players</h3>
                <p className="text-sm text-gray-600">Connect with board game enthusiasts actively searching for clubs</p>
              </div>
            </div>

            <div className="card bg-base-100 shadow-md">
              <div className="card-body items-center text-center">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                  <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                  </svg>
                </div>
                <h3 className="card-title text-lg mb-2">Easy Management</h3>
                <p className="text-sm text-gray-600">Manage bookings, events, and member interactions in one place</p>
              </div>
            </div>

            <div className="card bg-base-100 shadow-md">
              <div className="card-body items-center text-center">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                  <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <h3 className="card-title text-lg mb-2">Grow Your Community</h3>
                <p className="text-sm text-gray-600">Build a thriving club with our tools and support</p>
              </div>
            </div>
          </div>

          {/* Main Form */}
          <div className="card bg-base-100 shadow-xl max-w-4xl mx-auto">
            <div className="card-body">
              <h2 className="card-title text-2xl justify-center mb-4">
                Register Your Board Game Club
              </h2>
              <p className="text-center text-gray-600 mb-6">
                Fill in the details below to get started
              </p>

              {submitted ? (
                <div className="text-center py-12">
                  <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-warning/10 mb-4">
                    <svg className="h-8 w-8 text-warning" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">Club Registration Submitted!</h3>
                  <div className="max-w-lg mx-auto space-y-4 text-left bg-base-200 p-6 rounded-lg mb-8">
                    <div className="flex items-start gap-3">
                      <svg className="w-5 h-5 text-info mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <p className="text-sm text-gray-700">
                        <strong>Verification in Process:</strong> Your club registration is currently under review by our team.
                      </p>
                    </div>
                    <div className="flex items-start gap-3">
                      <svg className="w-5 h-5 text-info mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                      <p className="text-sm text-gray-700">
                        <strong>Not Yet Visible:</strong> Your club will not appear in search results or the clubs page until it's verified.
                      </p>
                    </div>
                    <div className="flex items-start gap-3">
                      <svg className="w-5 h-5 text-success mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <p className="text-sm text-gray-700">
                        <strong>After Verification:</strong> Once approved, your club will be displayed on the homepage and clubs page for everyone to see and join.
                      </p>
                    </div>
                    <div className="flex items-start gap-3">
                      <svg className="w-5 h-5 text-info mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      <p className="text-sm text-gray-700">
                        <strong>We'll Notify You:</strong> You'll receive an email notification once your club is verified.
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => navigate('/')}
                    className="btn btn-primary"
                  >
                    Back to Home
                  </button>
                </div>
              ) : (
                <>
                  {error && (
                    <div className="alert alert-error mb-6">
                      <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span>{error}</span>
                    </div>
                  )}

                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Club Name */}
                    <div className="form-control">
                      <label className="label">
                        <span className="label-text font-semibold">Club Name *</span>
                      </label>
                      <input
                        type="text"
                        name="name"
                        id="name"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        className="input input-bordered w-full"
                        placeholder="e.g., Downtown Chess & Strategy Club"
                      />
                    </div>

                    {/* Location */}
                    <div className="form-control">
                      <label className="label">
                        <span className="label-text font-semibold">Location</span>
                      </label>
                      <input
                        type="text"
                        name="location"
                        id="location"
                        value={formData.location}
                        onChange={handleChange}
                        className="input input-bordered w-full"
                        placeholder="e.g., Downtown Library, 123 Main Street, City"
                      />
                      <label className="label">
                        <span className="label-text-alt text-gray-500">Help players find you easily</span>
                      </label>
                    </div>

                    {/* Image Upload */}
                    <div className="form-control">
                      <label className="label">
                        <span className="label-text font-semibold">Club Image</span>
                      </label>
                      
                      <div
                        className={`flex justify-center px-6 pt-5 pb-6 border-2 border-dashed rounded-lg transition-all ${
                          dragActive 
                            ? 'border-primary bg-primary/5' 
                            : 'border-gray-300 hover:border-primary/50'
                        }`}
                        onDragEnter={handleDrag}
                        onDragLeave={handleDrag}
                        onDragOver={handleDrag}
                        onDrop={handleDrop}
                      >
                        <div className="space-y-1 text-center w-full">
                          {imagePreview ? (
                            <div className="relative">
                              <img 
                                src={imagePreview} 
                                alt="Preview" 
                                className="mx-auto h-64 w-full object-cover rounded-lg"
                              />
                              <button
                                type="button"
                                onClick={removeImage}
                                className="btn btn-error btn-circle btn-sm absolute top-2 right-2"
                              >
                                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            </div>
                          ) : (
                            <>
                              <svg
                                className="mx-auto h-16 w-16 text-gray-400"
                                stroke="currentColor"
                                fill="none"
                                viewBox="0 0 48 48"
                              >
                                <path
                                  d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                                  strokeWidth={2}
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                              <div className="flex text-sm text-gray-600 justify-center">
                                <label
                                  htmlFor="image-upload"
                                  className="relative cursor-pointer font-medium text-primary hover:text-primary-focus"
                                >
                                  <span>Upload a file</span>
                                  <input
                                    id="image-upload"
                                    name="image-upload"
                                    type="file"
                                    accept="image/*"
                                    className="sr-only"
                                    onChange={handleFileInputChange}
                                  />
                                </label>
                                <p className="pl-1">or drag and drop</p>
                              </div>
                              <p className="text-xs text-gray-500">
                                PNG, JPG, GIF, WebP up to 5MB
                              </p>
                            </>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Description */}
                    <div className="form-control">
                      <label className="label">
                        <span className="label-text font-semibold">Club Description</span>
                      </label>
                      <textarea
                        name="description"
                        id="description"
                        rows={5}
                        value={formData.description}
                        onChange={handleChange}
                        className="textarea textarea-bordered w-full"
                        placeholder="Tell potential members about your club, what makes it special, and what they can expect..."
                      />
                      <label className="label">
                        <span className="label-text-alt text-gray-500">Share your club's story and atmosphere</span>
                      </label>
                    </div>

                    {/* Games */}
                    <div className="form-control">
                      <label className="label">
                        <span className="label-text font-semibold">Games Played</span>
                      </label>
                      <input
                        type="text"
                        name="games"
                        id="games"
                        value={formData.games}
                        onChange={handleChange}
                        className="input input-bordered w-full"
                        placeholder="e.g., Chess, Settlers of Catan, Ticket to Ride, Pandemic"
                      />
                      <label className="label">
                        <span className="label-text-alt text-gray-500">Separate multiple games with commas</span>
                      </label>
                    </div>

                    {/* Meeting Day */}
                    <div className="form-control">
                      <label className="label">
                        <span className="label-text font-semibold">Meeting Schedule</span>
                      </label>
                      <input
                        type="text"
                        name="meetingDay"
                        id="meetingDay"
                        value={formData.meetingDay}
                        onChange={handleChange}
                        className="input input-bordered w-full"
                        placeholder="e.g., Every Thursday 6:00 PM - 9:00 PM"
                      />
                      <label className="label">
                        <span className="label-text-alt text-gray-500">When do you meet? Be as specific as possible</span>
                      </label>
                    </div>

                    {/* Submit Button */}
                    <div className="pt-6 border-t border-gray-200">
                      <button
                        type="submit"
                        disabled={loading}
                        className="btn btn-primary w-full"
                      >
                        {loading ? (
                          <>
                            <span className="loading loading-spinner"></span>
                            Registering Your Club...
                          </>
                        ) : (
                          <>
                            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            Register My Club
                          </>
                        )}
                      </button>
                      <p className="mt-3 text-xs text-gray-500 text-center">
                        By registering, you agree to our terms and conditions
                      </p>
                    </div>
                  </form>
                </>
              )}
            </div>
          </div>

          {/* FAQ Section */}
          <div className="card bg-base-100 shadow-md mt-12 max-w-4xl mx-auto">
            <div className="card-body">
              <h3 className="card-title text-xl justify-center mb-6">
                Frequently Asked Questions
              </h3>
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Is there a fee to join?</h4>
                  <p className="text-gray-600 text-sm">BoardVoyage offers flexible pricing plans. Check our pricing page for details.</p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">How do I manage bookings?</h4>
                  <p className="text-gray-600 text-sm">Once registered, you'll have access to a dashboard where you can manage all bookings and member interactions.</p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Can I update my club information later?</h4>
                  <p className="text-gray-600 text-sm">Yes! You can edit your club profile anytime from your club dashboard.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default PartnerWithUs;
